import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

import NewPasswordForm from "fond/signIn/NewPasswordForm";
import { Wave } from "fond/svg_icons";
import { CognitoUser } from "fond/types";
import { PageCentred } from "fond/widgets/PageCentred";

import MFAChallengeForm from "./MFAChallengeForm";
import SignInForm from "./SignInForm";
import SignUpAdornment from "./SignUpAdornment";

/**
 * The top level component for the sign in route. Presents the SignInForm in a SignInContainer.
 * In the event that cognitoUser is set, then the NewPasswordForm is displayed instead.
 * If MFA is required, the MFAChallengeForm is displayed.
 * cognitoUser can be set by the SignInForm using the onNewPasswordRequired or onMFARequired callbacks.
 */
const SignIn: React.FC = () => {
  // the cognito user object for the current user. Required by the NewPasswordForm and MFAChallengeForm.
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
  const [mfaRequired, setMfaRequired] = useState(false);

  const handleMFARequired = (user: CognitoUser) => {
    setCognitoUser(user);
    setMfaRequired(true);
  };

  const getTitle = () => {
    if (mfaRequired) return "Verification required";
    return (
      <>
        Log in <Wave style={{ marginLeft: "8px" }} />
      </>
    );
  };

  const getDescription = () => {
    if (mfaRequired) return <Typography>Enter your authenticator code to continue.</Typography>;
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Typography>Welcome back!</Typography>
      </Box>
    );
  };

  const renderForm = () => {
    if (cognitoUser && mfaRequired) {
      return <MFAChallengeForm cognitoUser={cognitoUser} />;
    }
    if (cognitoUser) {
      return <NewPasswordForm cognitoUser={cognitoUser} />;
    }
    return <SignInForm onNewPasswordRequired={setCognitoUser} onMFARequired={handleMFARequired} />;
  };

  return (
    <PageCentred
      title={getTitle()}
      windowTitle={mfaRequired ? "Verification required" : "Log in"}
      description={getDescription()}
      rightAdornment={!mfaRequired ? <SignUpAdornment /> : undefined}
      bare
    >
      {renderForm()}
    </PageCentred>
  );
};

export default SignIn;
